/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  CONFIRMATION: 'confirmation',
  EXECUTION: 'execution',
  CLEAR_FAILED: 'clearFailed',
  FAILED: 'failed',
  SUCCESS: 'success'
};

export default {
  name: 'requirement055',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      // ダイアログフラグ
      modal_requirement_055: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.CLEAR_FAILED:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.CONFIRMATION:
        case PHASE.EXECUTION:
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_055 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_055 = false;
    },

    /** OKボタン押下 */
    async exitProcess() {
      await this.callExitProcess();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 初期処理 */
    async start() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            this.phase = PHASE.CONFIRMATION;
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 */
    async execute() {
      try {
        this.phase = PHASE.EXECUTION;
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        let exitResponse = null;
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // DefaultSession遷移実行
              exitResponse = await this.exit();
              // exitResponseが存在しない場合、共通エラーに遷移
              if (exitResponse) {
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                if (exitResponse.data.status === config.STATUS_WSP_FAILED) {
                  this.showUnexpectedError();
                  break;
                }
              } else {
                this.showUnexpectedError();
                break;
              }
              this.phase = PHASE.SUCCESS;
              break;
            default:
              // DefaultSession遷移実行
              exitResponse = await this.exit();
              // exitResponseが存在しない場合、共通エラーに遷移
              if (exitResponse) {
                if (exitResponse.data.status === config.STATUS_WSP_FAILED) {
                  this.showUnexpectedError();
                  break;
                }
              } else {
                this.showUnexpectedError();
                break;
              }
              this.phase = PHASE.CLEAR_FAILED;
              break;
          }
        } else {
          // DefaultSession遷移実行
          exitResponse = await this.exit();
          // exitResponseが存在しない場合、共通エラーに遷移
          if (exitResponse) {
            if (exitResponse.data.status === config.STATUS_WSP_FAILED) {
              this.showUnexpectedError();
              return;
            }
          } else {
            this.showUnexpectedError();
            return;
          }
          this.phase = PHASE.CLEAR_FAILED;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit() {
      const token = session.getToken();
      // Exit API
      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'exit'
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
    },

    /** ボタンイベントから呼び出される終了処理 */
    async callExitProcess() {
      try {
        this.isLoading = true;
        // DefaultSession遷移実行
        const response = await this.exit();
        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            await this.closeWizard();
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // 終了処理API呼び出し
      const token = session.getToken();
      individualWorkSupportUtil.pollingWspApi(
        this, this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'exit'
        },
        // 共通エラーではエラー発生時でも何もしない
      );
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
    await this.start();
  },
};